import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ProgressReportApiService } from '../../../core/api-services/progress-report-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import { ProgressReport, ProgressReportStatus } from '../../../core/models/progress-report';
import { Scholarship } from '../../../core/models/scholarship';

/**
 * Reviewer queue for student academic progress reports.
 *
 * Reports are per student, per scholarship, so a reviewer can see everyone
 * awarded for a cycle. A report is approved, sent back for resubmission, or
 * rejected — the feedback goes straight to the student.
 */
@Component({
  selector: 'app-admin-progress-reports',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './progress-reports.html',
  styleUrl: './progress-reports.scss',
})
export class AdminProgressReportsComponent implements OnInit {
  private readonly api = inject(ProgressReportApiService);
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly fb = inject(FormBuilder);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  readonly statuses: ProgressReportStatus[] = ['SUBMITTED', 'APPROVED', 'RESUBMIT', 'REJECTED'];

  readonly reports = signal<ProgressReport[]>([]);
  readonly scholarships = signal<Scholarship[]>([]);
  readonly selected = signal<ProgressReport | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');

  readonly filters = this.fb.group({
    cycle: [''],
    scholarshipId: [null as number | null],
    status: [''],
    search: [''],
  });

  readonly reviewForm = this.fb.group({
    decision: ['Approve' as 'Approve' | 'Resubmit' | 'Reject'],
    feedback: ['', Validators.required],
  });

  ngOnInit(): void {
    this.load();
    this.scholarshipApi.getScholarships().subscribe({
      next: (scholarships) => this.scholarships.set(scholarships ?? []),
      error: () => this.scholarships.set([]),
    });
  }

  load(): void {
    const value = this.filters.getRawValue();
    this.loading.set(true);
    this.error.set('');

    this.api
      .getReportsForReview(
        value.cycle || null,
        value.scholarshipId ?? null,
        (value.status as ProgressReportStatus) || null,
        value.search ?? ''
      )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (reports) => this.reports.set(reports ?? []),
        error: () => this.error.set('The progress report queue could not be loaded.'),
      });
  }

  open(report: ProgressReport): void {
    this.error.set('');
    this.message.set('');
    this.selected.set(report);
    this.reviewForm.reset({ decision: 'Approve', feedback: '' });
  }

  close(): void {
    this.selected.set(null);
  }

  /** Approves, sends back for resubmission, or rejects the report. */
  review(): void {
    const report = this.selected();
    if (!report || this.busy()) {
      return;
    }

    const value = this.reviewForm.getRawValue();

    if (value.decision !== 'Approve' && !(value.feedback ?? '').trim()) {
      this.reviewForm.markAllAsTouched();
      this.error.set('Feedback is required when a report is not approved.');
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.api
      .review(report.id, { decision: value.decision ?? 'Approve', feedback: value.feedback ?? null })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (updated) => {
          this.selected.set(updated);
          this.message.set(`Progress report ${updated.referenceNumber} is now ${updated.status}.`);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The report could not be reviewed.'),
      });
  }

  download(report: ProgressReport): void {
    this.api.downloadReviewDocument(report.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = report.documentFileName || `progress-report-${report.referenceNumber}`;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.error.set('The academic record could not be downloaded.'),
    });
  }

  statusClass(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'status status-approved';
      case 'REJECTED':
        return 'status status-rejected';
      case 'RESUBMIT':
        return 'status status-booked';
      default:
        return 'status status-pending';
    }
  }

  canReview(report: ProgressReport): boolean {
    return report.status === 'SUBMITTED';
  }
}
